// Singleton dispatcher for the in-app confirm dialog.
// Replaces `window.confirm(...)` / `confirm(...)` everywhere in the app so
// that the UX stays consistent between the web preview and the Electron
// desktop shell (which renders native browser dialogs differently).

export type ConfirmVariant = "default" | "destructive";

export interface ConfirmDialogOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

export interface ConfirmDialogRequest extends ConfirmDialogOptions {
  id: number;
  resolve: (confirmed: boolean) => void;
}

type Listener = (req: ConfirmDialogRequest | null) => void;

let listener: Listener | null = null;
let nextId = 1;
let current: ConfirmDialogRequest | null = null;

export function subscribeConfirmDialog(fn: Listener): () => void {
  listener = fn;
  if (current) fn(current);
  return () => {
    if (listener === fn) listener = null;
  };
}

export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  // Auto-cancel a previous pending request if any.
  if (current) {
    current.resolve(false);
    current = null;
  }
  return new Promise<boolean>((resolve) => {
    current = {
      ...options,
      id: nextId++,
      resolve: (v) => {
        current = null;
        listener?.(null);
        resolve(v);
      },
    };
    if (listener) {
      listener(current);
    } else {
      // Fail-safe: if no host is mounted, don't block the app; treat as cancel.
      console.warn("[confirmDialog] No host mounted, defaulting to cancel.");
      const c = current;
      current = null;
      resolve(false);
      void c;
    }
  });
}
